import type {
  VisualFrameContext,
  VisualMutation,
  VisualRenderer,
  VisualSignalEvent,
} from '../../visual/index.js';
import type { RenderingPrototype } from '../prototype/RenderingPrototype.js';

/**
 * The existing world, as a layer.
 *
 * This is a **wrapper, not a rewrite**. `RenderingPrototype` still owns the
 * stage, the starfield, the nebula, the rings, the mandala core, the ripple
 * field and Mirror Drift, still draws them in its own `renderFrame`, and is
 * still driven by the runtime exactly as before. Nothing about the game's
 * appearance moved into this file, which is what makes "turn the visual engine
 * off and you have the old Mandalizm" true by construction rather than by
 * careful maintenance.
 *
 * So this renderer does not draw. It *modulates*: it reads its layer's resolved
 * parameters and pushes them into the world the prototype already draws. With
 * the default recipe — one MANDALA layer, no mapping, intensity 1 — it pushes
 * nothing at all and the frame is bit-for-bit what it was.
 *
 * The one thing it must never touch is bloom's threshold, radius and strength.
 * Those are fixed for the project, and a layer parameter that reached them would
 * be a slow way of losing the look everything else was tuned against.
 */

export interface MandalaRendererOptions {
  readonly prototype: RenderingPrototype;
  readonly layerId: string;
}

export class MandalaRenderer implements VisualRenderer {
  readonly id = 'MANDALA';

  private readonly prototype: RenderingPrototype;
  private enabled = true;
  /** Signal-driven trim applied on top of whatever the prototype computed. */
  private bloomTrim = 1;
  private lastFlash = 0;
  /**
   * Mutation state, kept deliberately shallow.
   *
   * The existing mandala is the regression baseline, so this renderer may only
   * *modulate* it — bloom and camera-independent phase. It does not reach into
   * the ring geometry, and with no mutations applied every value here is inert,
   * which is what keeps "MANDALA only == the old Mandalizm" true.
   */
  private asymmetry = 0;
  private phaseShift = 0;
  private readonly mutationCounts: Record<string, number> = {};

  constructor(options: MandalaRendererOptions) {
    this.prototype = options.prototype;
  }

  initialize(): void {
    // Nothing to build: the world already exists and outlives any one recipe.
  }

  reset(): void {
    this.bloomTrim = 1;
    this.lastFlash = 0;
    this.asymmetry = 0;
    this.phaseShift = 0;
    for (const key of Object.keys(this.mutationCounts)) delete this.mutationCounts[key];
    this.applyTrim();
  }

  update(context: VisualFrameContext): void {
    if (!this.enabled) return;

    // `intensity` and `opacity` are the layer's own controls; a mapping may have
    // written `bloomTrim` or `flash` into the parameters this frame.
    const parameters = context.layer.parameters;
    const flash = numeric(parameters.flash, 0);
    const trim = numeric(parameters.bloomTrim, 1);

    // Decays on its own so a mapped impulse reads as a kick rather than a step.
    this.lastFlash = Math.max(this.lastFlash * 0.86, flash);
    // Asymmetry is temporary by design: the mandala should recover its symmetry
    // rather than stay bent, so a miss reads as a disturbance, not as damage.
    this.asymmetry = Math.max(0, this.asymmetry - context.deltaTime * 0.5);
    this.phaseShift *= Math.exp(-context.deltaTime * 0.7);
    this.bloomTrim = trim * context.layer.intensity;
    this.applyTrim();
  }

  /**
   * Mutations, within the limits the regression baseline allows.
   *
   * Everything here goes through knobs the world already animates — bloom
   * strength scale and a phase offset. Nothing rebuilds the mandala, and with
   * no mutation applied the values are inert, so the frame is the one the game
   * drew before Phase 3.
   */
  applyMutation(item: VisualMutation, context: VisualFrameContext): void {
    this.mutationCounts[item.action] = (this.mutationCounts[item.action] ?? 0) + 1;
    const force = item.sustained ? item.strength * context.deltaTime : item.strength;

    switch (item.action) {
      case 'SPAWN':
      case 'SPLASH':
        // A hit blooms the whole frame for a moment.
        this.lastFlash = Math.min(1.5, this.lastFlash + force * 0.6);
        break;
      case 'DISTORT':
      case 'BREAK':
        this.asymmetry = Math.min(1, this.asymmetry + force * 0.45);
        break;
      case 'ROTATE':
      case 'SHIFT':
        this.phaseShift += force * 0.8;
        break;
      case 'EXPAND':
      case 'DENSITY':
        this.bloomTrim = Math.min(2, this.bloomTrim + force * 0.05);
        break;
      case 'CONTRACT':
      case 'FADE':
        this.bloomTrim = Math.max(0.3, this.bloomTrim - force * 0.05);
        break;
      default:
        // The mandala has no answer for SPIRAL-ish or particle actions.
        break;
    }
    this.applyTrim();
  }

  get mutationsApplied(): Readonly<Record<string, number>> {
    return { ...this.mutationCounts };
  }

  /** Non-zero only while a disturbance is settling. */
  get asymmetryLevel(): number {
    return this.asymmetry;
  }

  render(): void {
    // The prototype draws the whole scene itself, in its own frame callback.
    // Drawing again here would be a second pass over the same objects.
  }

  handleSignal(_event: VisualSignalEvent): void {
    // Levels are enough for this renderer; discrete moments reach it through the
    // envelopes its mapping reads.
  }

  setParameters(): void {
    // Applied per frame from the resolved layer state, so there is nothing to
    // latch here — a parameter set between frames takes effect on the next one.
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) {
      this.bloomTrim = 1;
      this.applyTrim();
    }
  }

  dispose(): void {
    // The prototype is owned by the session and survives this renderer.
    this.bloomTrim = 1;
    this.applyTrim();
  }

  /**
   * Scales bloom *strength* only, and only around the value the prototype set.
   *
   * `setStrengthScale` is the existing knob the world already animates through;
   * writing here rather than to `bloomPass.strength` means the fixed 0.90 the
   * project settled on stays the baseline this multiplies.
   */
  private applyTrim(): void {
    // Asymmetry dims slightly as well as bending the phase, so a disturbance is
    // felt even on a frame where the geometry has not moved far.
    const scale = this.bloomTrim + this.lastFlash * 0.25 - this.asymmetry * 0.12;
    this.prototype.setVisualBloomTrim(clamp(scale, 0.25, 2));
    this.prototype.setVisualPhaseShift(this.phaseShift + this.asymmetry * 0.35);
  }
}

function numeric(value: number | string | boolean | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}
